/* eslint-env browser */
import Url from 'url';

import _ from 'lodash';
import * as Sentry from '@sentry/browser';

import React from 'react';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';

import { isBetaHost } from 'shared/util';

import { retry, dlog, derror } from 'frontend/util';

import SimcVersionSingleton from 'frontend/simcVersion';

import createStore from './state/store';
import actions from './state/actions';
import rootReducer from './state/reducers';

const url = Url.parse(window.location.href, true);

// local storage

let initialState;
if (window.initialData) {
  // get default reducer keys
  const defaultData = rootReducer();


  initialState = {
    ...window.initialData,
    // remove unknown user fields before putting into the reducer
    user: _.pick(window.initialData.user, _.keys(defaultData.user)),
  };

  const user = _.get(initialState, 'user');
  if (user && user.key) {
    Sentry.setUser({
      id: user.key,
      username: user.username,
      email: user.email,
    });
  }
}
// isStorageAvailable sources from MDN on 2019-08-22
// eslint-disable-next-line
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Feature-detecting_localStorage
const isStorageAvailable = type => {
  let storage;
  try {
    storage = window[type];
    const key = '__raidbots_storage_test__';
    storage.setItem(key, key);
    storage.removeItem(key);
    return true;
  } catch (e) {
    return e instanceof DOMException && (
      e.code === 22 ||
      e.code === 1014 ||
      e.name === 'QuotaExceededError' ||
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      (storage && storage.length !== 0);
  }
};

if (isStorageAvailable('localStorage')) {
  try {
    if (window.localStorage && window.localStorage.getItem) {
      const localStore = window.localStorage.getItem('store');
      if (localStore) {
        const localData = JSON.parse(localStore);

        const stateVersion = _.get(localData, 'version.stateVersion');

        if (stateVersion <= 10) {
          // delete broken talent overrides that contain NaNs
          const talentOverridePath = 'simbot.character.talents';
          const overrides = _.get(localData, talentOverridePath);
          if (overrides && overrides.match(/NaN/)) {
            _.set(localData, talentOverridePath, null);
          }

          // update state version
          _.set(localData, 'version.stateVersion', 11);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 11) {
          // force sim expiration / privacy policy overlay
          _.set(localData, 'site.overlay', 'sim-expiration');

          // update state version
          _.set(localData, 'version.stateVersion', 12);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 12) {
          // set detailed progress as default
          _.set(localData, 'report.detailedProgress', true);

          // update state version
          _.set(localData, 'version.stateVersion', 13);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 13) {
          // sigh, actually set the correct value
          _.set(localData, 'simbot.report.detailedProgress', true);

          // update state version
          _.set(localData, 'version.stateVersion', 14);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 14) {
          // disable 8.2 options
          _.set(localData, 'simbot.simcOptions.nazjatar', false);
          _.set(localData, 'simbot.simcOptions.aberration', false);

          // set this back to 0 - I think group-based toggles like this should probably be low/0 by default
          _.set(localData, 'simbot.simcOptions.worldveinAllies', 0);

          // nudge users back to smart sim
          _.set(localData, 'simbot.simcOptions.iterations', 'smart');

          // update state version
          _.set(localData, 'version.stateVersion', 15);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 15) {
          // remove warforgeLevel in droptimizer M+ instanceOptions as these no longer WF/TF in 8.3
          const mythicOptionsIdx = _.findIndex(
            _.get(localData, 'simbot.droptimizer.instanceOptions'), { instanceId: -1 },
          );
          if (mythicOptionsIdx > -1) {
            localData.simbot.droptimizer.instanceOptions[mythicOptionsIdx].warforgeLevel = {};
          }

          // update state version
          _.set(localData, 'version.stateVersion', 16);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 16) {
          // update surging vitality values
          const svMap = {
            35: 12,
            26: 8,
            21: 6,
            16: 5,
            13: 4,
            5: 3,
          };
          const oldSurgingValue = _.get(localData, 'simbot.simcOptions.surgingVitality');
          if (oldSurgingValue) {
            _.set(localData, 'simbot.simcOptions.surgingVitality', svMap[oldSurgingValue] || 0);
          }
          // update state version
          _.set(localData, 'version.stateVersion', 17);
          window.localStorage.setItem('store', JSON.stringify(localData));
        }

        if (stateVersion <= 17) {
          // Shadowlands data resets

          _.set(localData, 'simbot.optimize.enhancements.selectedEnhancements', []);
          _.set(localData, 'simbot.optimize.enhancements.forceMainStat', []);
          _.set(localData, 'simbot.optimize.enhancements.wrathionSocket', []);

          _.set(localData, 'simbot.droptimizer.selectedInstance', null);
          _.set(localData, 'simbot.droptimizer.instanceOptions', []);
          _.set(localData, 'simbot.droptimizer.instances', []);
          _.set(localData, 'simbot.droptimizer.encounters', []);
          _.set(localData, 'simbot.droptimizer.difficulty', 'raid-normal');
          _.set(localData, 'simbot.droptimizer.allAzeritePowers', false);

          _.set(localData, 'simbot.simcOptions.iterations', 'smart');
          _.set(localData, 'simbot.simcOptions.potion', '');
          _.set(localData, 'simbot.simcOptions.food', '');
          _.set(localData, 'simbot.simcOptions.flask', '');
          _.set(localData, 'simbot.simcOptions.augmentation', '');

          _.set(localData, 'site.overlay', 'sl-notes');
          _.set(localData, 'version.stateVersion', 18);
        }

        if (stateVersion <= 18) {
          // clear APLs, should have done this at SL launch
          _.set(localData, 'simbot.customApls.customApls', []);

          _.set(localData, 'version.stateVersion', 19);
        }

        if (stateVersion <= 19) {
          // Reduce max conduit rank override to current max obtainable rank in-game
          const croPath = 'simbot.optimize.covenants.conduitRankOverride';
          if (_.get(localData, croPath) > 9) {
            _.set(localData, croPath, 9);
          }

          _.set(localData, 'version.stateVersion', 20);
        }

        if (stateVersion <= 20) {
          // fix titanic gland default, got deployed at 0 accidentally
          const ocularGlandPath = 'simbot.simcOptions.ocularGlandUptime';
          if (_.get(localData, ocularGlandPath) === 0) {
            _.set(localData, ocularGlandPath, 100);
          }

          _.set(localData, 'version.stateVersion', 21);
        }

        if (stateVersion <= 21) {
          const domShardBonusPath = 'simbot.simcOptions.enableRuneWords';
          _.set(localData, domShardBonusPath, false);
          _.set(localData, 'version.stateVersion', 22);
        }

        if (stateVersion <= 22) {
          // deselect any domination shards. 9.2 updates just removed the UI but didn't actually unselect the shards
          const domShardIds = [
            187057, 187284, 187293, 187302, 187312, 187059, 187285, 187294, 187303, 187313, 187061, 187286, 187295,
            187304, 187314, 187063, 187287, 187296, 187305, 187315, 187071, 187289, 187298, 187307, 187317, 187065,
            187288, 187297, 187306, 187316, 187073, 187290, 187299, 187308, 187318, 187079, 187292, 187301, 187310,
            187320, 187076, 187291, 187300, 187309, 187319,
          ];
          const enhancementsPath = 'simbot.optimize.enhancements.selectedEnhancements';
          const noDomShards = _.reject(
            _.get(localData, enhancementsPath),
            enh => _.includes(domShardIds, enh.enhancementId),
          );
          _.set(localData, enhancementsPath, noDomShards);

          // just set this one more time since there was a brief beta deployment that hit live which could have
          // incorrectly set the stateVersion ahead
          const domShardBonusPath = 'simbot.simcOptions.enableRuneWords';
          _.set(localData, domShardBonusPath, false);

          _.set(localData, 'version.stateVersion', 23);
        }

        // Dragonflight!
        // Remove old /simc input
        if (stateVersion <= 23) {
          // clear out old /simc input
          _.set(localData, 'simbot.form.text', '');

          _.set(localData, 'simbot.optimize.text', '');
          _.set(localData, 'simbot.optimize.preloadData', '');

          _.set(localData, 'version.stateVersion', 24);
        }

        // Prepatch cleanse
        if (stateVersion <= 24) {
          // clear out old /simc input again, just to make sure
          _.set(localData, 'simbot.form.text', '');

          _.set(localData, 'simbot.optimize.text', '');
          _.set(localData, 'simbot.optimize.preloadData', '');
          _.set(localData, 'simbot.optimize.talents', { selected: [], locked: [] });

          // APLs
          _.set(localData, 'simbot.customApls.customApls', []);

          // armory character info
          _.set(localData, 'simbot.character.data', null);
          _.set(localData, 'simbot.character.talents', null);
          _.set(localData, 'simbot.character.talentsModalOpen', false);

          _.set(localData, 'simbot.simcExpertOptions.header', '');
          _.set(localData, 'simbot.simcExpertOptions.preActors', '');
          _.set(localData, 'simbot.simcExpertOptions.postActors', '');
          _.set(localData, 'simbot.simcExpertOptions.footer', '');

          _.set(localData, 'version.stateVersion', 25);
        }

        if (stateVersion <= 25) {
          // reset fight style if it's one that has been archived
          const unsupportedStyles = [
            'Ultraxion',
            'Beastlord',
            'HelterSkelter',
          ];

          if (_.includes(unsupportedStyles, localData.simbot?.simcOptions?.fightStyle)) {
            _.set(localData, 'simbot.simcOptions.fightStyle', 'Patchwerk');
          }

          _.set(localData, 'version.stateVersion', 26);
        }

        if (stateVersion <= 26) {
          // weird deployment set this on live for a bunch of people, this clears it back out
          _.set(localData, 'site.customBuild', null);

          _.set(localData, 'version.stateVersion', 27);
        }

        if (stateVersion <= 27) {
          // hiding iterations option in DF so reset it to smart
          _.set(localData, 'simbot.simcOptions.iterations', 'smart');

          _.set(localData, 'version.stateVersion', 28);
        }

        if (stateVersion <= 28) {
          // Dragonflight data resets

          _.set(localData, 'simbot.optimize.enhancements.selectedEnhancements', []);
          _.set(localData, 'simbot.optimize.enhancements.forceMainStat', []);
          _.set(localData, 'simbot.optimize.enhancements.wrathionSocket', []);
          _.set(localData, 'simbot.optimize.enhancements.addSocketNum', 0);
          _.set(localData, 'simbot.optimize.enhancements.addSocketGem', []);
          _.set(localData, 'simbot.optimize.enhancementReplace', false);
          _.set(localData, 'simbot.optimize.tierSetRequirement', 0);
          _.set(localData, 'simbot.optimize.upgradeCurrencyConstraints', {});

          _.set(localData, 'simbot.droptimizer.selectedInstance', null);
          _.set(localData, 'simbot.droptimizer.instanceOptions', []);
          _.set(localData, 'simbot.droptimizer.lootSpec', {});
          _.set(localData, 'simbot.droptimizer.instances', []);
          _.set(localData, 'simbot.droptimizer.encounters', []);
          _.set(localData, 'simbot.droptimizer.difficulty', 'raid-normal');
          _.set(localData, 'simbot.droptimizer.allAzeritePowers', false);

          _.set(localData, 'simbot.simcOptions.potion', '');
          _.set(localData, 'simbot.simcOptions.food', '');
          _.set(localData, 'simbot.simcOptions.flask', '');
          _.set(localData, 'simbot.simcOptions.augmentation', '');
          _.set(localData, 'simbot.simcOptions.ptr', false);
          _.set(localData, 'simbot.simcOptions.temporaryEnchant', '');

          _.set(localData, 'version.stateVersion', 29);
        }

        if (stateVersion <= 29) {
          _.set(localData, 'simbot.simcOptions.whisperingIncarnateIconRoles', 'dps/heal/tank');

          _.set(localData, 'version.stateVersion', 30);
        }

        // corrupting rage simc default changed to a lower value
        if (stateVersion <= 30) {
          const rageUptime = localData?.simbot?.simcOptions?.corruptingRageUptime;
          if (rageUptime === 70) {
            _.set(localData, 'simbot.simcOptions.corruptingRageUptime', 50);
          }

          _.set(localData, 'version.stateVersion', 31);
        }

        // corrupting rage simc default changed again
        if (stateVersion <= 31) {
          const rageUptime = localData?.simbot?.simcOptions?.corruptingRageUptime;
          if (rageUptime === 50) {
            _.set(localData, 'simbot.simcOptions.corruptingRageUptime', 80);
          }

          _.set(localData, 'version.stateVersion', 32);
        }

        // ensure initial version data is empty
        if (stateVersion <= 32) {
          _.set(localData, 'version.initialVersion', undefined);
          _.set(localData, 'version.initialVersionUpdated', undefined);
          _.set(localData, 'version.initialGameDataVersion', undefined);
          _.set(localData, 'version.stateVersion', 33);
        }

        if (stateVersion <= 33) {
          const blueSilkenLining = localData?.simbot?.simcOptions?.blueSilkenLining;
          if (blueSilkenLining === 70) {
            _.set(localData, 'simbot.simcOptions.blueSilkenLining', 40);
          }

          _.set(localData, 'version.stateVersion', 34);
        }

        if (stateVersion <= 34) {
          // The War Within data resets

          _.set(localData, 'simbot.simcOptions.potion', '');
          _.set(localData, 'simbot.simcOptions.food', '');
          _.set(localData, 'simbot.simcOptions.flask', '');
          _.set(localData, 'simbot.simcOptions.augmentation', '');
          _.set(localData, 'simbot.simcOptions.temporaryEnchant', '');
          _.set(localData, 'simbot.simcOptions.ptr', false);

          _.set(localData, 'simbot.optimize.enhancements.selectedEnhancements', []);
          _.set(localData, 'simbot.optimize.enhancements.forceMainStat', []);
          _.set(localData, 'simbot.optimize.enhancements.wrathionSocket', []);
          _.set(localData, 'simbot.optimize.enhancements.addSocketNum', 0);
          _.set(localData, 'simbot.optimize.enhancements.addSocketGem', []);
          _.set(localData, 'simbot.optimize.enhancementReplace', false);
          _.set(localData, 'simbot.optimize.enhancementAdvancedControls', false);
          _.set(localData, 'simbot.optimize.tierSetRequirement', 0);
          _.set(localData, 'simbot.optimize.upgradeCurrencyConstraints', {});
          _.set(localData, 'simbot.optimize.itemSetRequirements', {});

          _.set(localData, 'simbot.droptimizer.selectedInstance', null);
          _.set(localData, 'simbot.droptimizer.instanceOptions', []);
          _.set(localData, 'simbot.droptimizer.lootSpec', {});
          _.set(localData, 'simbot.droptimizer.instances', []);
          _.set(localData, 'simbot.droptimizer.encounters', []);
          _.set(localData, 'simbot.droptimizer.difficulty', 'raid-normal');
          _.set(localData, 'simbot.droptimizer.allAzeritePowers', false);

          _.set(localData, 'version.stateVersion', 35);
        }

        initialState = _.defaults({}, localData, initialState);
      }
    }
  } catch (ex) {
    // can't use local storage for some reason
    derror('Unable to load store from localStorage, settings will not be saved across sessions');

    Sentry.captureException(ex);
  }
}

if (url.query.region) {
  _.set(initialState, 'simbot.form.armoryRegion', url.query.region.toLowerCase());
  _.set(initialState, 'simbot.character.data', null);
}
if (url.query.realm) {
  _.set(initialState, 'simbot.form.armoryRealm', url.query.realm.toLowerCase());
  _.set(initialState, 'simbot.character.data', null);
}
if (url.query.name) {
  _.set(initialState, 'simbot.form.armoryName', url.query.name);
  _.set(initialState, 'simbot.character.data', null);
}

SimcVersionSingleton.set(_.get(initialState, 'simbot.simcOptions.simcVersion', 'weekly'));

let store;

// status interval
// do not run if using a custom/beta build
if (url.query.build) {
  dlog('Custom build, do not run status interval');
  _.set(initialState, 'site.customBuild', url.query.build);
} else if (isBetaHost(_.get(window, 'location.hostname'))) {
  dlog('Beta build, do not run status interval');
  _.set(initialState, 'site.customBuild', _.get(window.initialData, 'version.initialVersion'));
} else if (window.raidbotsMinimalMode) {
  // don't run interval or log anything
} else {
  // run every 5 minutes + (0-30) seconds
  const interval = (5 * 60 * 1000) + (1000 * _.random(0, 32));
  setInterval(() => actions.fetchStatus()(store.dispatch), interval);

  initialState.version.initialVersion = window.initialData?.version?.initialVersion;
  initialState.version.initialVersionUpdated = window.initialData?.version?.initialVersionUpdated;
  initialState.version.initialGameDataVersion = window.initialData?.version?.initialGameDataVersion;
}

store = createStore(initialState);

const RaidbotsRouter = React.lazy(() => retry(() => import('./components/Router')));

const App = () => (
  <HelmetProvider>
    <Provider store={store}>
      <RaidbotsRouter />
    </Provider>
  </HelmetProvider>
);

export default App;
